import React, { useState, useEffect } from 'react';
import Card from './Card';
import Button from './Button';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import DropdownButton from './DropdownButton';
import FurnitureCatalogPicker from './FurnitureCatalogPicker';
import CharacterOutfitCatalogPicker from './CharacterOutfitCatalogPicker';
import RoomSurfaceCatalogPicker from './RoomSurfaceCatalogPicker';
import ToggleSwitch from './ToggleSwitch';
import CloseIcon from '../icons/CloseIcon';
import PlusCircleIcon from '../icons/PlusCircleIcon';
import PlusIcon from '../icons/PlusIcon';
import { type CharacterLayerSlot } from '../../types/character';
import { FEATURE_CHARACTER_COSMETIC } from '../../config/featureFlags';
import { getCharacterShopOutfit, type CharacterShopOutfitId } from '../../utils/characterPresets';
import IllustratedCharacter from '../character/IllustratedCharacter';
import { hasPetItemEffect, PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY, type PetItemEffect, type PetPlayActionType } from '../../types/petStore';
import { getFurniturePreset, getRoomSurfaceImage, getRoomSurfacePreset, type FurnitureKey, type RoomSurfaceKey } from '../../types/myHome';
import {
  getDefaultCategoryForStoreType,
  getItemCategoryOptionsForStoreType,
  type StoreTypeId,
} from '../../utils/storeTypes';

type StoreItemCategory =
  | 'usage'
  | 'qr'
  | 'seatReservation'
  | 'seatTakeover'
  | 'collectible'
  | 'gacha'
  | 'cosmetic'
  | 'furniture'
  | 'roomSurface'
  | typeof PET_CONSUMABLE_CATEGORY
  | typeof PET_PLAY_TICKET_CATEGORY;

const PET_PLAY_ACTION_OPTIONS: { label: string; value: PetPlayActionType }[] = [
  { label: '쓰다듬기', value: 'stroke' },
  { label: '공 던져주기', value: 'ball' },
  { label: '깃털 놀기', value: 'feather' },
  { label: '레이저 놀기', value: 'laser' }
];

const PET_EFFECT_FIELDS: Array<{ key: keyof PetItemEffect; label: string; hint: string }> = [
  { key: 'hunger', label: '배고픔', hint: '음수면 감소 (예: -20)' },
  { key: 'happiness', label: '행복', hint: '양수면 증가' },
  { key: 'energy', label: '에너지', hint: '양수면 증가' },
  { key: 'health', label: '건강', hint: '양수면 증가' },
  { key: 'exp', label: 'EXP', hint: '경험치' },
  { key: 'intimacy', label: '친밀도', hint: '친밀도' }
];

interface EditStoreItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id?: string;
    name?: string;
    description?: string;
    itemCategory?: StoreItemCategory;
    petEffect?: PetItemEffect;
    petPlayAction?: PetPlayActionType;
    characterSlot?: CharacterLayerSlot;
    characterOutfitId?: CharacterShopOutfitId;
    furnitureKey?: FurnitureKey;
    roomSurfaceKey?: RoomSurfaceKey;
    gachaItems?: Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>;
    gachaAnimationType?: 'scratch' | 'card' | 'maru';
    usageType?: string;
    price?: number;
    hasSalePeriod?: boolean;
    salePeriodStart?: string;
    salePeriodEnd?: string;
    hasStockLimit?: boolean;
    stock?: number;
    isLimited?: boolean;
    imageUrl?: string;
    isActive?: boolean;
  };
  onSave: (itemData: {
    name: string;
    description: string;
    itemCategory: StoreItemCategory;
    petEffect?: PetItemEffect;
    petPlayAction?: PetPlayActionType;
    characterSlot?: CharacterLayerSlot;
    characterOutfitId?: CharacterShopOutfitId;
    furnitureKey?: FurnitureKey;
    roomSurfaceKey?: RoomSurfaceKey;
    gachaItems?: Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>;
    gachaAnimationType?: 'scratch' | 'card' | 'maru';
    usageType?: string;
    price: number;
    hasSalePeriod: boolean;
    salePeriodStart?: string;
    salePeriodEnd?: string;
    hasStockLimit: boolean;
    stock: number;
    isLimited: boolean;
    imageUrl?: string;
    isActive: boolean;
  }) => void;
  onFileUpload?: (file: File) => Promise<string>;
  usageTypes?: Array<{ id: string; label: string; imageUrl?: string }>;
  availableItems?: Array<{ id: string; name: string; imageUrl?: string; isActive?: boolean }>; // 뽑기에 포함할 수 있는 아이템 목록
  /** 생성 시 노출할 아이템 종류를 상점 유형으로 제한 */
  storeType?: StoreTypeId;
}

const EditStoreItemDialog: React.FC<EditStoreItemDialogProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  onSave,
  onFileUpload,
  usageTypes = [],
  availableItems = [],
  storeType
}) => {
  const [itemCategory, setItemCategory] = useState<StoreItemCategory | ''>('');
  const [petEffect, setPetEffect] = useState<PetItemEffect>({
    hunger: -20,
    happiness: 10,
    energy: 0,
    health: 0,
    exp: 0,
    intimacy: 0
  });
  const [petPlayAction, setPetPlayAction] = useState<PetPlayActionType>('stroke');
  const [characterSlot, setCharacterSlot] = useState<CharacterLayerSlot | ''>('');
  const [characterOutfitId, setCharacterOutfitId] = useState<CharacterShopOutfitId | ''>('');
  const [furnitureKey, setFurnitureKey] = useState<FurnitureKey | ''>('');
  const [roomSurfaceKey, setRoomSurfaceKey] = useState<RoomSurfaceKey | ''>('');
  const [gachaItems, setGachaItems] = useState<Array<{ itemId?: string; itemName: string; count: number; imageUrl?: string; isBlank?: boolean }>>([]);
  const [gachaAnimationType, setGachaAnimationType] = useState<'scratch' | 'card' | 'maru'>('scratch');
  const [usageType, setUsageType] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [hasSalePeriod, setHasSalePeriod] = useState(false);
  const [salePeriodStart, setSalePeriodStart] = useState('');
  const [salePeriodEnd, setSalePeriodEnd] = useState('');
  const [hasStockLimit, setHasStockLimit] = useState(false);
  const [stock, setStock] = useState('1');
  const [isLimited, setIsLimited] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setItemCategory(initialData.itemCategory || 'usage');
      setUsageType(initialData.usageType || '');
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setPrice(initialData.price?.toString() || '');
      setHasSalePeriod(initialData.hasSalePeriod || false);
      setSalePeriodStart(initialData.salePeriodStart || '');
      setSalePeriodEnd(initialData.salePeriodEnd || '');
      setHasStockLimit(initialData.hasStockLimit || false);
      setStock(initialData.stock?.toString() || '1');
      setIsLimited(initialData.isLimited || false);
      setImageUrl(initialData.imageUrl || '');
      setIsActive(initialData.isActive !== undefined ? initialData.isActive : true);
      setGachaItems((initialData.gachaItems || []).map(item => ({
        ...item,
        count: item.count ?? 1
      })));
      setGachaAnimationType(initialData.gachaAnimationType || 'scratch');
      setCharacterSlot(initialData.characterSlot || '');
      setCharacterOutfitId(initialData.characterOutfitId || '');
      setFurnitureKey(initialData.furnitureKey || '');
      setRoomSurfaceKey(initialData.roomSurfaceKey || '');
      setPetEffect({
        hunger: initialData.petEffect?.hunger ?? -20,
        happiness: initialData.petEffect?.happiness ?? 10,
        energy: initialData.petEffect?.energy ?? 0,
        health: initialData.petEffect?.health ?? 0,
        exp: initialData.petEffect?.exp ?? 0,
        intimacy: initialData.petEffect?.intimacy ?? 0
      });
      setPetPlayAction(initialData.petPlayAction || 'stroke');
    } else {
      // 새 아이템 생성 모드일 때 초기화
      setItemCategory(storeType ? getDefaultCategoryForStoreType(storeType) : '');
      setUsageType('');
      setName('');
      setDescription('');
      setPrice('');
      setHasSalePeriod(false);
      setSalePeriodStart('');
      setSalePeriodEnd('');
      setHasStockLimit(false);
      setStock('1');
      setIsLimited(storeType === 'clothing' || storeType === 'furniture');
      setImageUrl('');
      setIsActive(true);
      setGachaItems([]);
      setGachaAnimationType('scratch');
      setCharacterSlot('');
      setCharacterOutfitId('');
      setFurnitureKey('');
      setRoomSurfaceKey('');
      setPetEffect({
        hunger: -20,
        happiness: 10,
        energy: 0,
        health: 0,
        exp: 0,
        intimacy: 0
      });
      setPetPlayAction('stroke');
    }
  }, [mode, initialData, isOpen, storeType]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!onFileUpload) {
      alert('파일 업로드 기능을 사용할 수 없습니다.');
      return;
    }

    // 파일 타입 검사
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      e.target.value = ''; // 입력 필드 초기화
      return;
    }

    // 파일 크기 제한 (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('파일 크기는 10MB를 초과할 수 없습니다.');
      e.target.value = ''; // 입력 필드 초기화
      return;
    }

    try {
      setUploadingImage(true);
      const url = await onFileUpload(file);
      setImageUrl(url);
    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      const errorMessage = error?.message || '이미지 업로드에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setUploadingImage(false);
      // 입력 필드 초기화 (같은 파일을 다시 선택할 수 있도록)
      e.target.value = '';
    }
  };

  const handleCharacterOutfitSelect = (value: string) => {
    const outfit = getCharacterShopOutfit(value);
    setCharacterOutfitId((outfit?.id as CharacterShopOutfitId) || '');
    if (!outfit) return;
    setCharacterSlot('base');
    setImageUrl(outfit.shopImageUrl || outfit.boyImageUrl || outfit.girlImageUrl);
    if (!name.trim() || name === getCharacterShopOutfit(characterOutfitId)?.name) setName(outfit.name);
    if (!description.trim() || description === getCharacterShopOutfit(characterOutfitId)?.description) setDescription(outfit.description);
    setIsLimited(true);
  };

  const handleFurnitureSelect = (value: string) => {
    const furniture = getFurniturePreset(value);
    if (!furniture) return;
    setFurnitureKey(furniture.key);
    setImageUrl(furniture.imageUrl);
    if (!name.trim() || name === getFurniturePreset(furnitureKey)?.label) setName(furniture.label);
    if (!description.trim()) setDescription(`마이홈의 ${furniture.slot === 'wall' ? '벽' : furniture.slot === 'floor' ? '바닥' : furniture.slot === 'desk' ? '책상 자리' : furniture.slot === 'bed' ? '침대 자리' : '방 한쪽'}에 배치할 수 있는 가구입니다.`);
    setIsLimited(true);
  };
  const handleRoomSurfaceSelect = (value: string) => {
    const surface = getRoomSurfacePreset(value);
    if (!surface) return;
    setRoomSurfaceKey(surface.key);
    setImageUrl(getRoomSurfaceImage(surface.key) || '');
    if (!name.trim() || name === getRoomSurfacePreset(roomSurfaceKey)?.label) setName(surface.label);
    const previousSurface = getRoomSurfacePreset(roomSurfaceKey);
    const previousDescription = previousSurface ? `마이홈의 ${previousSurface.slot === 'wallpaper' ? '벽지' : '바닥재'}를 바꾸는 꾸미기 아이템입니다.` : '';
    if (!description.trim() || description === previousDescription) setDescription(`마이홈의 ${surface.slot === 'wallpaper' ? '벽지' : '바닥재'}를 바꾸는 꾸미기 아이템입니다.`);
    setIsLimited(true);
  };

  const selectedCharacterOutfit = getCharacterShopOutfit(characterOutfitId);

  const renderCharacterOutfitPreview = () => selectedCharacterOutfit ? (
    <div style={{
      padding: '12px',
      borderRadius: '12px',
      background: 'var(--Color-background-alternative, #F7F7F7)',
      border: '1px solid var(--Color-line-neutral, #E5E5E5)'
    }}>
      <div style={{
        marginBottom: '8px',
        color: 'var(--Color-text-neutral, #555)',
        fontSize: '13px',
        fontWeight: 600
      }}>
        {selectedCharacterOutfit.id === 'orange-star-jacket' ? 'SD 상의 · 얼굴과 머리 고정' : 'SD 세트 · 기준 얼굴 유지'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: selectedCharacterOutfit.boyImageUrl ? '1fr 1fr' : '1fr', gap: '8px' }}>
        {[
          { label: '남학생', imageUrl: selectedCharacterOutfit.boyImageUrl },
          { label: '여학생', imageUrl: selectedCharacterOutfit.girlImageUrl }
        ].filter((preview) => preview.imageUrl).map((preview) => (
          <div key={preview.label} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px',
            borderRadius: '8px',
            background: '#FFFFFF'
          }}>
            <IllustratedCharacter
              src={preview.imageUrl}
              alt={`${selectedCharacterOutfit.name} ${preview.label} 미리보기`}
              style={{ width: '100%', height: '112px', objectFit: 'contain' }}
            />
            <span style={{ color: '#555', fontSize: '12px', fontWeight: 600 }}>{preview.label}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '8px', color: '#767676', fontSize: '12px', lineHeight: '18px' }}>
        {selectedCharacterOutfit.id === 'orange-star-jacket' ? '새 기본 캐릭터의 상의만 교체합니다. 청바지와 운동화는 유지됩니다.' : selectedCharacterOutfit.boyImageUrl ? '새 SD 캐릭터 기준의 한 벌 복장입니다. 의상·하의·신발을 함께 착용합니다.' : '여학생 전용 SD 세트 복장입니다.'} 구매한 복장은 옷장에 보관되며 다시 입을 수 있어요. 가격은 교사가 아래에서 정해 주세요.
      </div>
    </div>
  ) : null;

  const handleSave = () => {
    if (!itemCategory) {
      alert('아이템 종류를 선택해주세요.');
      return;
    }
    if (!name.trim()) {
      alert('아이템 이름을 입력해주세요.');
      return;
    }
    if (!description.trim()) {
      alert('아이템 설명을 입력해주세요.');
      return;
    }
    if (!price || isNaN(Number(price)) || Number(price) <= 0) {
      alert('올바른 가격을 입력해주세요.');
      return;
    }
    if (hasSalePeriod && (!salePeriodStart || !salePeriodEnd)) {
      alert('판매 기간을 모두 선택해주세요.');
      return;
    }
    if (hasStockLimit && (!stock || isNaN(Number(stock)) || Number(stock) < 0)) {
      alert('올바른 재고 수량을 입력해주세요.');
      return;
    }
    if (itemCategory === 'gacha' && gachaItems.length === 0) {
      alert('뽑기 아이템에는 최소 1개 이상의 아이템을 추가해주세요.');
      return;
    }
    if (itemCategory === 'gacha') {
      const totalCount = gachaItems.reduce((sum, item) => sum + (item.count ?? 1), 0);
      if (totalCount <= 0) {
        alert('뽑기 아이템의 총 개수가 1개 이상이어야 합니다.');
        return;
      }
    }
    if (itemCategory === 'cosmetic' && !characterOutfitId) {
      alert('판매할 캐릭터 복장을 선택해 주세요.');
      return;
    }
    if (itemCategory === 'furniture' && !furnitureKey) {
      alert('판매할 마이홈 가구를 선택해 주세요.');
      return;
    }
    if (itemCategory === 'roomSurface' && !roomSurfaceKey) { alert('판매할 벽지 또는 바닥재를 선택해 주세요.'); return; }
    if (itemCategory === PET_CONSUMABLE_CATEGORY && !hasPetItemEffect(petEffect)) {
      alert('캐릭터 간식은 효과 수치를 최소 1개 이상 설정해 주세요.');
      return;
    }

    if (itemCategory === PET_PLAY_TICKET_CATEGORY && !petPlayAction) {
      alert('놀아주기 종류를 선택해 주세요.');
      return;
    }

    // 뽑기 아이템인 경우 재고를 gachaItems의 총 count 합계로 자동 계산
    let calculatedStock = hasStockLimit ? Number(stock) : 0;
    if (itemCategory === 'gacha' && gachaItems.length > 0) {
      calculatedStock = gachaItems.reduce((sum, item) => sum + (item.count ?? 1), 0);
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      itemCategory: itemCategory as StoreItemCategory,
      usageType: itemCategory === 'usage' ? usageType : undefined,
      petEffect: itemCategory === PET_CONSUMABLE_CATEGORY ? petEffect : undefined,
      petPlayAction: itemCategory === PET_PLAY_TICKET_CATEGORY ? petPlayAction : undefined,
      gachaItems: itemCategory === 'gacha' ? gachaItems : undefined,
      gachaAnimationType: itemCategory === 'gacha' ? gachaAnimationType : undefined,
      characterSlot: itemCategory === 'cosmetic' ? 'base' : undefined,
      characterOutfitId: itemCategory === 'cosmetic' && characterOutfitId ? characterOutfitId : undefined,
      furnitureKey: itemCategory === 'furniture' && furnitureKey ? furnitureKey : undefined,
      roomSurfaceKey: itemCategory === 'roomSurface' && roomSurfaceKey ? roomSurfaceKey : undefined,
      price: Number(price),
      hasSalePeriod,
      salePeriodStart: hasSalePeriod ? salePeriodStart : undefined,
      salePeriodEnd: hasSalePeriod ? salePeriodEnd : undefined,
      hasStockLimit: itemCategory === 'gacha' ? true : hasStockLimit, // 뽑기 아이템은 항상 재고 제한 적용
      stock: calculatedStock,
      isLimited,
      imageUrl: imageUrl || undefined,
      isActive
    });
  };

  const handleStockIncrease = () => {
    setStock((prev) => (Number(prev) + 1).toString());
  };

  const handleStockDecrease = () => {
    setStock((prev) => Math.max(0, Number(prev) - 1).toString());
  };

  if (!isOpen) return null;

  const itemCategoryOptions = storeType
    ? getItemCategoryOptionsForStoreType(storeType)
    : [
        { label: '사용권', value: 'usage' as const },
        { label: 'QR 코드', value: 'qr' as const },
        { label: '좌석 예약권', value: 'seatReservation' as const },
        { label: '좌석 강탈권', value: 'seatTakeover' as const },
        { label: '소장용', value: 'collectible' as const },
        { label: '뽑기', value: 'gacha' as const },
        { label: '캐릭터 간식', value: PET_CONSUMABLE_CATEGORY },
        { label: '캐릭터 놀아주기 이용권', value: PET_PLAY_TICKET_CATEGORY },
        ...(FEATURE_CHARACTER_COSMETIC ? [{ label: '캐릭터 복장', value: 'cosmetic' as const }] : []),
        { label: '마이홈 가구', value: 'furniture' as const },
        { label: '마이홈 벽지·바닥', value: 'roomSurface' as const },
      ];
  const hideCategorySelect = mode === 'create' && itemCategoryOptions.length === 1;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="688px"
        padding="24px 28px"
        borderRadius="16px"
        background="var(--Color-background-normal, white)"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '90vh',
          maxHeight: '800px',
          overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          flex: 1,
          overflowY: 'auto',
          paddingRight: '8px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              width: '592px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <h2 style={{
                margin: 0,
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '20px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                {mode === 'create' ? '새 아이템 생성하기' : '아이템 정보 수정하기'}
              </h2>
              {mode === 'create' && (
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '16px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '24px'
                }}>
                  학급 상점에서 판매 할 아이템을 생성해 주세요.
                </div>
              )}
            </div>
            
            {/* 닫기 버튼 */}
            <div 
              style={{
                width: '32px',
                height: '32px',
                padding: '4px',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={onClose}
            >
              <CloseIcon width={13.20} height={13.20} color="#0D0D0D" />
            </div>
          </div>

          {/* 폼 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            {/* 이미지 및 기본 정보 영역 */}
            {imageUrl ? (
              // 이미지가 있는 경우: 이미지 왼쪽, 정보 오른쪽
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                {/* 이미지 미리보기 */}
                <div style={{
                  position: 'relative'
                }}>
                <img 
                  src={imageUrl} 
                  alt="아이템 이미지"
                  style={{
                    width: '175px',
                    height: '144px',
                    borderRadius: '8px',
                    objectFit: 'cover'
                  }}
                />
                  {/* 이미지 변경 버튼 */}
                  <div 
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => document.getElementById('item-image-upload')?.click()}
                  >
                    <PlusCircleIcon width={20} height={20} color="#FFFFFF" />
                  </div>
                  <input
                    id="item-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                </div>
                
                {/* 종류, 이름, 설명 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {!hideCategorySelect && (
                    <DropdownButton
                      options={itemCategoryOptions}
                      value={itemCategory}
                      onChange={(value) => {
                        setItemCategory(value as any);
                        if (value !== 'usage') {
                          setUsageType('');
                        }
                        if (value === 'cosmetic') {
                          setIsLimited(true);
                          setHasStockLimit(true);
                          setStock('1');
                          setCharacterSlot('base');
                        }
                        if (value === 'furniture') setIsLimited(true);
                        if (value === 'roomSurface') setIsLimited(true);
                      }}
                      placeholder="아이템 종류"
                      width="119px"
                      height="48px"
                    />
                    )}
                    <InputField
                      value={name}
                      onChange={setName}
                      placeholder="아이템 이름을 입력해 주세요"
                      width={hideCategorySelect ? '447px' : '320px'}
                      height="48px"
                    />
                  </div>
                  
                  {itemCategory === 'usage' && usageTypes.length > 0 && (
                    <DropdownButton
                      options={usageTypes.map(type => ({ label: type.label, value: type.id }))}
                      value={usageType}
                      onChange={(value) => {
                        setUsageType(value);
                        // 사용 유형 선택 시 해당 이미지 자동 적용
                        const selectedUsageType = usageTypes.find(type => type.id === value);
                        if (selectedUsageType?.imageUrl && !imageUrl) {
                          setImageUrl(selectedUsageType.imageUrl);
                        }
                      }}
                      placeholder="사용 유형 선택"
                      width="100%"
                      height="48px"
                    />
                  )}

                  {itemCategory === 'cosmetic' && (
                    <CharacterOutfitCatalogPicker value={characterOutfitId} onChange={handleCharacterOutfitSelect} />
                  )}
                  {itemCategory === 'furniture' && (
                    <FurnitureCatalogPicker value={furnitureKey} onChange={handleFurnitureSelect} />
                  )}
                  {itemCategory === 'roomSurface' && <RoomSurfaceCatalogPicker value={roomSurfaceKey} onChange={handleRoomSurfaceSelect} />}
                  {itemCategory === 'cosmetic' && renderCharacterOutfitPreview()}
                  {itemCategory === 'cosmetic' && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--Color-text-assistive, #989898)',
                        lineHeight: '18px',
                        fontFamily: 'pretendard'
                      }}
                    >
                      복장을 선택하면 남·여 착용 이미지와 상점용 썸네일이 자동으로 적용됩니다.
                    </div>
                  )}
                  
                  {itemCategory === PET_CONSUMABLE_CATEGORY && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--Color-text-normal, #161616)' }}>
                        캐릭터 허브 효과
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--Color-text-assistive, #989898)', lineHeight: '18px' }}>
                        학생이 캐릭터 허브에서 사용할 때 적용됩니다. 배고픔은 음수로 입력하면 줄어듭니다.
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        {PET_EFFECT_FIELDS.map(({ key, label, hint }) => (
                          <InputField
                            key={key}
                            label={label}
                            value={String(petEffect[key] ?? 0)}
                            onChange={(value) =>
                              setPetEffect((prev) => ({ ...prev, [key]: Number(value) || 0 }))
                            }
                            placeholder={hint}
                            size="small"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {itemCategory === PET_PLAY_TICKET_CATEGORY && (
                    <DropdownButton
                      options={PET_PLAY_ACTION_OPTIONS}
                      value={petPlayAction}
                      onChange={(value) => setPetPlayAction(value as PetPlayActionType)}
                      placeholder="놀아주기 종류"
                      width="100%"
                      height="48px"
                    />
                  )}

                  <TextAreaField
                    value={description}
                    onChange={setDescription}
                    placeholder="아이템에 대한 설명을 입력해 주세요."
                    width="100%"
                    height="84px"
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              // 이미지 없는 경우: 세로 레이아웃
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <DropdownButton
                    options={itemCategoryOptions}
                    value={itemCategory}
                    onChange={(value) => {
                      setItemCategory(value as any);
                      if (value !== 'usage') {
                        setUsageType('');
                      }
                      if (value === 'cosmetic') {
                        setIsLimited(true);
                        setHasStockLimit(true);
                        setStock('1');
                        setCharacterSlot('base');
                      }
                      if (value === 'furniture') setIsLimited(true);
                      if (value === 'roomSurface') setIsLimited(true);
                    }}
                    placeholder="아이템 종류"
                    width="128px"
                    height="48px"
                  />
                  <InputField
                    value={name}
                    onChange={setName}
                    placeholder="아이템 이름을 입력해 주세요"
                    width="496px"
                    height="48px"
                  />
                </div>
                
                {itemCategory === 'usage' && usageTypes.length > 0 && (
                  <DropdownButton
                    options={usageTypes.map(type => ({ label: type.label, value: type.id }))}
                    value={usageType}
                    onChange={(value) => {
                      setUsageType(value);
                      // 사용 유형 선택 시 해당 이미지 자동 적용
                      const selectedUsageType = usageTypes.find(type => type.id === value);
                      if (selectedUsageType?.imageUrl && !imageUrl) {
                        setImageUrl(selectedUsageType.imageUrl);
                      }
                    }}
                    placeholder="사용 유형 선택"
                    width="100%"
                    height="48px"
                  />
                )}

                {itemCategory === 'cosmetic' && (
                  <CharacterOutfitCatalogPicker value={characterOutfitId} onChange={handleCharacterOutfitSelect} />
                )}
                {itemCategory === 'furniture' && (
                  <FurnitureCatalogPicker value={furnitureKey} onChange={handleFurnitureSelect} />
                )}
                {itemCategory === 'roomSurface' && <RoomSurfaceCatalogPicker value={roomSurfaceKey} onChange={handleRoomSurfaceSelect} />}
                {itemCategory === 'cosmetic' && renderCharacterOutfitPreview()}
                {itemCategory === 'cosmetic' && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--Color-text-assistive, #989898)',
                      lineHeight: '18px',
                      fontFamily: 'pretendard'
                    }}
                  >
                    복장을 선택하면 남·여 착용 이미지와 상점용 썸네일이 자동으로 적용됩니다.
                  </div>
                )}
                
                {itemCategory === PET_CONSUMABLE_CATEGORY && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--Color-text-normal, #161616)' }}>
                      캐릭터 허브 효과
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--Color-text-assistive, #989898)', lineHeight: '18px' }}>
                      학생이 캐릭터 허브에서 사용할 때 적용됩니다. 배고픔은 음수로 입력하면 줄어듭니다.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {PET_EFFECT_FIELDS.map(({ key, label, hint }) => (
                        <InputField
                          key={key}
                          label={label}
                          value={String(petEffect[key] ?? 0)}
                          onChange={(value) =>
                            setPetEffect((prev) => ({ ...prev, [key]: Number(value) || 0 }))
                          }
                          placeholder={hint}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {itemCategory === PET_PLAY_TICKET_CATEGORY && (
                  <DropdownButton
                    options={PET_PLAY_ACTION_OPTIONS}
                    value={petPlayAction}
                    onChange={(value) => setPetPlayAction(value as PetPlayActionType)}
                    placeholder="놀아주기 종류"
                    width="100%"
                    height="48px"
                  />
                )}

                <TextAreaField
                  value={description}
                  onChange={setDescription}
                  placeholder="아이템에 대한 설명을 입력해 주세요."
                  width="100%"
                  height="84px"
                  rows={3}
                />
                
                {/* 이미지 업로드 버튼 */}
                <div style={{
                  height: '48px',
                  padding: '9px 264px',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onClick={() => document.getElementById('item-image-upload')?.click()}
                >
                  <input
                    id="item-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <PlusCircleIcon width={20} height={20} color="#989898" />
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      {uploadingImage ? '업로드 중...' : '아이템 이미지 업로드'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

            {/* 가격 및 판매 기간 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '28px'
            }}>
              {/* 가격 */}
              <div style={{
                width: '210px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  height: '25px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '24px'
                }}>
                  가격
                </div>
                <InputField
                  type="number"
                  value={price}
                  onChange={setPrice}
                  placeholder="가격 입력"
                  width="100%"
                  height="48px"
                />
              </div>

              {/* 판매 기간 설정 */}
              <div style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                minHeight: '85px'  // 고정 높이: 제목(25px) + 간격(12px) + 날짜입력(48px) = 85px
              }}>
                {/* 제목과 토글 - 항상 상단에 고정 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '12px',
                  height: '25px'
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '16px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: '24px'
                  }}>
                    판매 기간 설정
                  </div>
                  <ToggleSwitch
                    checked={hasSalePeriod}
                    onChange={setHasSalePeriod}
                    size="small"
                  />
                </div>
                
                {/* 날짜 입력 필드 - 조건부 표시 */}
                {hasSalePeriod && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <InputField
                      type="date"
                      value={salePeriodStart}
                      onChange={setSalePeriodStart}
                      placeholder="시작 날짜 선택"
                      width="50%"
                      height="48px"
                    />
                    <InputField
                      type="date"
                      value={salePeriodEnd}
                      onChange={setSalePeriodEnd}
                      placeholder="종료 날짜 선택"
                      width="50%"
                      height="48px"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 재고 제한 및 1회 구매 제한 */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '28px'
            }}>
              {/* 재고 제한 설정 - 뽑기 아이템이 아닌 경우에만 표시 */}
              {itemCategory !== 'gacha' && (
                <div style={{
                  width: '210px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '16px',
                      fontFamily: 'pretendard',
                      fontWeight: '600',
                      lineHeight: '24px'
                    }}>
                      재고 제한 설정
                    </div>
                    <ToggleSwitch
                      checked={hasStockLimit}
                      onChange={setHasStockLimit}
                      size="small"
                    />
                  </div>
                
                {hasStockLimit && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{
                      flex: '1',
                      padding: '12px 16px',
                      background: 'var(--Color-fill-white, white)',
                      borderRadius: '8px',
                      border: '1.20px solid var(--Color-line-normal, #F0F0F0)',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {/* 마이너스 버튼 */}
                      <div 
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={handleStockDecrease}
                      >
                        <div style={{
                          width: '13.20px',
                          height: '1.20px',
                          background: 'var(--Color-text-strong, #0D0D0D)'
                        }} />
                      </div>
                      
                      {/* 재고 수량 */}
                      <input
                        type="number"
                        value={stock}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
                          setStock(numValue.toString());
                        }}
                        onFocus={(e) => e.target.select()}
                        style={{
                          flex: '1',
                          textAlign: 'center',
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'pretendard',
                          fontWeight: '500',
                          lineHeight: '19.50px',
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          outline: 'none'
                        }}
                        min="0"
                      />
                      
                      {/* 플러스 버튼 */}
                      <div 
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={handleStockIncrease}
                      >
                        <PlusIcon width={13.20} height={13.20} fill="#0D0D0D" />
                      </div>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* 뽑기 아이템인 경우 재고 자동 표시 */}
              {itemCategory === 'gacha' && (
                <div style={{
                  width: '210px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '16px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: '24px'
                  }}>
                    총 뽑기 가능 횟수
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'pretendard',
                    fontWeight: '500'
                  }}>
                    {gachaItems.reduce((sum, item) => sum + (item.count ?? 1), 0)}개
                  </div>
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    뽑기 아이템의 재고는 뽑기 항목 개수 합계로 자동 계산됩니다.
                  </div>
                </div>
              )}

              {/* 1회 구매 제한 */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '24px'
                }}>
                  1회 구매 제한
                </div>
                <ToggleSwitch
                  checked={isLimited}
                  onChange={setIsLimited}
                  size="small"
                />
              </div>
            </div>

            {/* 판매 활성화 (수정 모드에서만 표시) */}
            {mode === 'edit' && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '24px'
                }}>
                  판매 활성화
                </div>
                <ToggleSwitch
                  checked={isActive}
                  onChange={setIsActive}
                  size="small"
                />
              </div>
            )}

            {/* 뽑기 아이템 설정 */}
            {itemCategory === 'gacha' && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '24px'
                }}>
                  뽑기 아이템 설정
          </div>
        
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {gachaItems.map((gachaItem, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: 'var(--Color-background-sub, #F8F8F8)',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        background: gachaItem.isBlank ? '#FFEBEE' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        {gachaItem.isBlank ? (
                          <span style={{ fontSize: '24px' }}>❌</span>
                        ) : gachaItem.imageUrl ? (
                          <img 
                            src={gachaItem.imageUrl} 
                            alt={gachaItem.itemName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '24px' }}>📦</span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'pretendard',
                          fontWeight: '500',
                          lineHeight: '21px'
                        }}>
                          {gachaItem.itemName}
                        </div>
                      </div>
                      <InputField
                        type="number"
                        value={(gachaItem.count ?? 1).toString()}
                        onChange={(value) => {
                          const newGachaItems = [...gachaItems];
                          newGachaItems[index] = {
                            ...newGachaItems[index],
                            count: Math.max(1, Number(value) || 1)
                          };
                          setGachaItems(newGachaItems);
                        }}
                        placeholder="개수"
                        width="80px"
                        height="40px"
                      />
                      <span style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        fontWeight: '500'
                      }}>
                        개
                      </span>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: 'white',
                          border: '1px solid var(--Color-line-normal, #F0F0F0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setGachaItems(gachaItems.filter((_, i) => i !== index));
                        }}
                      >
                        <span style={{ fontSize: '20px' }}>×</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 아이템 추가 */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  {availableItems.length > 0 && (
                    <select
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--Color-line-normal, #F0F0F0)',
                        fontSize: '14px',
                        fontFamily: 'pretendard',
                        background: 'white'
                      }}
                      value=""
                      onChange={(e) => {
                        const selectedItem = availableItems.find(item => item.id === e.target.value);
                        if (selectedItem && !gachaItems.find(gi => gi.itemId === selectedItem.id)) {
                          setGachaItems([...gachaItems, {
                            itemId: selectedItem.id,
                            itemName: selectedItem.name,
                            count: 1,
                            imageUrl: selectedItem.imageUrl,
                            isBlank: false
                          }]);
                        }
                        e.target.value = '';
                      }}
                    >
                      <option value="">아이템 선택</option>
                      {availableItems
                        .filter(item => !gachaItems.find(gi => gi.itemId === item.id))
                        .map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name}{item.isActive === false ? ' (비활성화)' : ''}
                          </option>
                        ))}
                    </select>
                  )}
                  <Button
                    width="auto"
                    height="40px"
                    variant="submit"
                    onClick={() => {
                      if (!gachaItems.find(gi => gi.isBlank)) {
                        setGachaItems([...gachaItems, {
                          itemName: '꽝',
                          count: 1,
                          isBlank: true
                        }]);
                      } else {
                        alert('꽝은 하나만 추가할 수 있습니다.');
                      }
                    }}
                  >
                    꽝 추가
                  </Button>
                </div>

                {/* 총 개수 표시 */}
                {gachaItems.length > 0 && (
                  <div style={{
                    padding: '12px',
                    background: '#E8F5E9',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <span style={{
                      color: '#2E7D32',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '500'
                    }}>
                      총 개수: {gachaItems.reduce((sum, item) => sum + (item.count ?? 1), 0)}개
                    </span>
                  </div>
                )}

                {/* 애니메이션 타입 선택 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '16px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: '24px'
                  }}>
                    애니메이션 타입
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {[
                      { value: 'scratch', label: '스크래치' },
                      { value: 'card', label: '카드 뽑기' },
                      { value: 'maru', label: 'MARU 로고' }
                    ].map((option) => (
                      <div
                        key={option.value}
                        style={{
                          flex: 1,
                          minWidth: '100px',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          background: gachaAnimationType === option.value 
                            ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                            : 'var(--Color-background-sub, #F8F8F8)',
                          color: gachaAnimationType === option.value 
                            ? 'white' 
                            : 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'pretendard',
                          fontWeight: '500',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => setGachaAnimationType(option.value as 'scratch' | 'card' | 'maru')}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          paddingTop: '16px',
          borderTop: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}>
          <Button
            width="211px"
            height="48px"
            variant="cancel"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            width="413px"
            height="48px"
            variant="submit"
            onClick={handleSave}
          >
            {mode === 'create' ? '등록' : '수정'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EditStoreItemDialog;
